import React, { useState } from 'react';

import * as Sentry from '@sentry/browser';
import { loadStripe } from '@stripe/stripe-js';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Checkbox from '@material-ui/core/Checkbox';

import { loadCurrentBillingUsage } from './actions/load-current-billing-usage';
import { updateSubscription } from './actions/update-subscription';
import { ItemizedPlanSelectorBody } from './itemized-plan-selector-body';
import { getBillingInfo } from '../../actions/get-billing-info';

import * as Api from '../../../api/api';
import { UiButton } from '../../../ui/ui-button';

import { STRIPE_PUBLIC_KEY } from '../../../config/config';
import { ACCOUNT_STATUS } from '../../../util/account-status';
import { isValueANumber } from '../../../util/number-utils';
import { SubscriptionCadence } from '../../../util/subscription-cadence';
import Message from '../../../widgets/legacy-message';
import { PRICING_VERSION } from '../../utils/pricing-version';

type InputProps = {
  account: TeamEngineAccount;
  subscriptionPriceItems: Array<PriceItemV3>;
  onClose: () => void;
  org?: Org;
  fromStatusTransition?: boolean;
  handleTransitionConfirmed?: (extraInfo: {}) => Promise<void>;
  isEdit?: boolean;
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { account }: InputProps,
) => ({
  actions: {
    loadBillingInfo: () => dispatch(getBillingInfo(account.accountId)),
    updateSubscription: (subscriptionUpdate: SubscriptionUpdateV3) =>
      dispatch(updateSubscription(account.accountId, subscriptionUpdate)),
    getCurrentUsage: () => dispatch(loadCurrentBillingUsage(account.accountId)),
  },
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & DispatchProps;

export const ItemizedPlanSelector = connect(
  undefined,
  mapDispatchToProps,
)((props: Props) => {
  const {
    account,
    subscriptionPriceItems,
    onClose,
    org,
    fromStatusTransition,
    handleTransitionConfirmed,
    isEdit,
    actions,
  } = props;

  // For now, subscriptionCadence can only be Monthly for v3, but boilerplate code is left
  // to make it easy to add Annual support in the future
  const [selectedSubscriptionCadence, setSelectedSubscriptionCadence] =
    useState<SubscriptionCadence>(SubscriptionCadence.MONTHLY);
  const [activeEmployeeCount, setActiveEmployeeCount] = useState<number | null>(
    subscriptionPriceItems.find((item) => item.itemizedPriceType === 'activeEmployees')?.quantity ||
      null,
  );
  const [activeJobCount, setActiveJobCount] = useState<number | null>(
    subscriptionPriceItems.find((item) => item.itemizedPriceType === 'activeJobs')?.quantity ||
      null,
  );
  const [additionalOrgCount, setAdditionalOrgCount] = useState<number | null>(
    subscriptionPriceItems.find((item) => item.itemizedPriceType === 'additionalOrgs')?.quantity ||
      null,
  );

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingUsage, setIsLoadingUsage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isPartnerReferral, setIsPartnerReferral] = useState<boolean>(
    Boolean(account?.isPartnerReferral),
  );

  const handleChangeEmployeeCount = (newEmployeeCount: string | null) => {
    const empCount = isValueANumber(newEmployeeCount) ? parseInt(newEmployeeCount, 10) : null;
    setActiveEmployeeCount(empCount);
  };

  const handleChangeJobCount = (newJobCount: string | null) => {
    const jobCount = isValueANumber(newJobCount) ? parseInt(newJobCount, 10) : null;
    setActiveJobCount(jobCount);
  };

  const handleChangeAdditionalOrgCount = (newOrgCount: string | null) => {
    const orgCount = isValueANumber(newOrgCount) ? parseInt(newOrgCount, 10) : null;
    setAdditionalOrgCount(orgCount);
  };

  const handleOnclose = () => {
    onClose();
  };

  const handleIsPartnerReferralChange = () => {
    setIsPartnerReferral(!isPartnerReferral);
  };

  const handleLoadCurrentUsage = async () => {
    setIsLoadingUsage(true);
    try {
      const result = await actions.getCurrentUsage();

      if (result) {
        setActiveJobCount(result.activeJobQuantity);
        setActiveEmployeeCount(result.activeEmployeeQuantity);
        setAdditionalOrgCount(result.additionalOrgQuantity);
      }
    } catch {
      setErrorMessage('Failed to load current usage');
    }
    setIsLoadingUsage(false);
  };

  const handleSavePlans = async () => {
    if (
      !selectedSubscriptionCadence ||
      selectedSubscriptionCadence !== SubscriptionCadence.MONTHLY
    ) {
      setErrorMessage('Invalid subscription cadence, please select monthly or annual.');
      return;
    }

    const subscriptionUpdate = {
      isPartnerReferral,
      subscriptionCadence: selectedSubscriptionCadence,
      activeJobQuantity: activeJobCount || 0,
      activeEmployeeQuantity: activeEmployeeCount || 0,
      additionalOrgQuantity: additionalOrgCount || 0,
    };

    setIsSaving(true);

    if (!isEdit && handleTransitionConfirmed) {
      // No payment info needed, so just execute the status and plan change
      await handleTransitionConfirmed({
        pricingVersionId: PRICING_VERSION.V3,
        isPartnerReferral,
        subscriptionCadence: selectedSubscriptionCadence,
        subscriptionUpdateV3: subscriptionUpdate,
      });
    } else {
      await actions.updateSubscription(subscriptionUpdate);
      await actions.loadBillingInfo();
    }

    if (
      !account.hasPaymentInfo &&
      org &&
      selectedSubscriptionCadence === SubscriptionCadence.MONTHLY &&
      fromStatusTransition
    ) {
      // Initiate a checkout (for the now updated plan) where credit card information will be collected
      const stripe = await loadStripe(STRIPE_PUBLIC_KEY);
      if (!stripe) {
        Sentry.captureMessage('Stripe checkout redirection failed');
        return;
      }
      const response = await Api.post(
        `/account/${account.accountId}/create-subscription-checkout-session`,
        {
          body: JSON.stringify({ orgId: org.org_id }),
        },
      );
      const session = await response.json();

      await stripe.redirectToCheckout({
        sessionId: session.id,
      });
    } else {
      setIsSaving(false);
      handleOnclose();
    }
  };

  return (
    <>
      {errorMessage ? (
        <Message variant="error" className="u-margin-bottom">
          {errorMessage}
        </Message>
      ) : null}
      <ItemizedPlanSelectorBody
        subscriptionCadence={selectedSubscriptionCadence}
        setSubscriptionCadence={setSelectedSubscriptionCadence}
        activeJobCount={activeJobCount}
        changeJobCount={handleChangeJobCount}
        activeEmployeeCount={activeEmployeeCount}
        changeEmployeeCount={handleChangeEmployeeCount}
        additionalOrgCount={additionalOrgCount}
        changeAdditionalOrgCount={handleChangeAdditionalOrgCount}
      />
      {fromStatusTransition &&
        [ACCOUNT_STATUS.PRE_TRIAL, ACCOUNT_STATUS.TRIAL, ACCOUNT_STATUS.TRIAL_LAPSED].includes(
          account.status,
        ) && (
          <div className="u-margin-top-sm u-flex-center">
            <Checkbox
              className="u-no-padding"
              checked={isPartnerReferral}
              onChange={handleIsPartnerReferralChange}
            />
            <div className="typo-title u-margin-left-md">
              This is a partner referral (33% off for 3 months)
            </div>
          </div>
        )}
      <div className="u-margin-top-lg u-display-flex u-justifyContent-spaceBetween">
        <UiButton
          variant="secondary"
          className="u-maxWidth-30"
          onClick={handleOnclose}
          disabled={isSaving || isLoadingUsage}
        >
          Cancel
        </UiButton>
        <UiButton
          variant="secondary"
          className="u-maxWidth-60"
          onClick={handleLoadCurrentUsage}
          disabled={isSaving}
          loading={isLoadingUsage}
        >
          Calculate Usage
        </UiButton>
        <UiButton
          onClick={handleSavePlans}
          className="u-margin-left-md u-maxWidth-30"
          disabled={isLoadingUsage}
          loading={isSaving}
        >
          Save
        </UiButton>
      </div>
    </>
  );
});
